/**
 * How long a note or rest lasts, in both of the two ways that matter.
 *
 * A duration has a *written* form -- a note head type, a number of dots and possibly a
 * tuplet -- and a *sounding* length in ticks. Neither one can be dropped. The written form
 * is what gets engraved and is the only thing that distinguishes a triplet quarter from
 * the eighth-plus-something it happens to equal; the tick length is what lets two parts be
 * lined up against each other, a measure be checked for completeness, or a score be played.
 *
 * The written form is the truth and the ticks are derived from it by {@link ticksOf}. A
 * duration is never constructed by picking a tick count and hoping a note type fits.
 *
 * ## Why 6720 ticks to the quarter
 *
 * The resolution has to make every duration the v1 editor can write land on a whole
 * number, because rounding a tick count turns a measure that is exactly full into one that
 * is a tick short, and the measure-completeness check would then reject music that is
 * correct. 6720 = 2^6 x 3 x 5 x 7, which covers the three prime tuplet divisions (triplets,
 * quintuplets, septuplets) against every note type down to the 64th, dotted and
 * double-dotted. Of the 144 combinations of type, dots and tuplet reachable here exactly
 * one is not a whole number -- the double-dotted 128th -- and that is not a duration this
 * editor offers.
 */

/** Written note-head types, longest first. */
export const NOTE_TYPES = [
  "whole",
  "half",
  "quarter",
  "eighth",
  "16th",
  "32nd",
  "64th",
  "128th",
] as const;

export type NoteType = (typeof NOTE_TYPES)[number];

/** Ticks in one quarter note. See the module docstring for why this number. */
export const PPQ = 6720;

/** Ticks in an undotted, untupleted note of each written type. */
const BASE_TICKS: Readonly<Record<NoteType, number>> = {
  whole: PPQ * 4,
  half: PPQ * 2,
  quarter: PPQ,
  eighth: PPQ / 2,
  "16th": PPQ / 4,
  "32nd": PPQ / 8,
  "64th": PPQ / 16,
  "128th": PPQ / 32,
};

/**
 * A tuplet, as the ratio the notation states: `actual` notes played in the time of
 * `normal` ones. A triplet is `{ actual: 3, normal: 2 }`.
 */
export interface TupletRatio {
  readonly actual: number;
  readonly normal: number;
}

/** The written duration of one event. */
export interface Duration {
  readonly type: NoteType;
  /** Augmentation dots. Each adds half of what stands before it. */
  readonly dots: number;
  /** `null` outside a tuplet. */
  readonly tuplet: TupletRatio | null;
}

/** How many ticks a written duration lasts. */
export function ticksOf(duration: Duration): number {
  const base = BASE_TICKS[duration.type];
  // Each dot adds half of the previous value: n dots multiply by 2 - 1/2^n.
  const dotted = base * (2 - 1 / 2 ** duration.dots);
  const tuplet = duration.tuplet;
  return tuplet === null ? dotted : (dotted * tuplet.normal) / tuplet.actual;
}

/** A duration written as plainly as possible: a note type, no dots, no tuplet. */
export function plain(type: NoteType): Duration {
  return { type, dots: 0, tuplet: null };
}

/** The same duration with a different number of dots. */
export function withDots(duration: Duration, dots: number): Duration {
  return { ...duration, dots: Math.max(0, Math.trunc(dots)) };
}

/** The same duration inside a tuplet, or out of one when given `null`. */
export function withTuplet(duration: Duration, tuplet: TupletRatio | null): Duration {
  return { ...duration, tuplet };
}

/** Whether every duration reachable from this one lands on a whole tick. */
export function isExact(duration: Duration): boolean {
  return Number.isInteger(ticksOf(duration));
}

/**
 * The plainest written duration that lasts exactly `ticks`, or `null` when no undotted or
 * dotted note type of any length does.
 *
 * Used when a duration has to be recovered from a tick count -- reading a MusicXML file
 * that states `<duration>` but no `<type>`, for instance. Tuplets are not guessed: a tick
 * count alone cannot distinguish a triplet from the tied notes that equal it, and guessing
 * would silently invent notation the source did not have.
 */
export function fromTicks(ticks: number): Duration | null {
  for (const type of NOTE_TYPES) {
    for (let dots = 0; dots <= 2; dots += 1) {
      const candidate: Duration = { type, dots, tuplet: null };
      if (ticksOf(candidate) === ticks) return candidate;
    }
  }
  return null;
}

/** Ticks in one measure of the given metre. */
export function measureTicks(beats: number, beatType: number): number {
  return (PPQ * 4 * beats) / beatType;
}

/** Every plain or dotted duration, longest first, for filling a span of time. */
const FILLERS: readonly Duration[] = NOTE_TYPES.flatMap((type) =>
  [2, 1, 0].map((dots) => ({ type, dots, tuplet: null })),
).sort((a, b) => ticksOf(b) - ticksOf(a));

/**
 * `ticks` written as as few notes as possible, longest first, or `null` when no combination
 * of plain and dotted note types adds up to it exactly.
 *
 * This is what pads a bar when an edit leaves a gap in it: deleting a note has to put
 * silence in its place, and writing a short note over a long one leaves a remainder that
 * has to be written as something. Taking the longest duration that still fits at each step
 * is what an engraver does, and it is why `null` is possible at all -- a remainder left by
 * a tuplet is not writable without tuplets of its own, and inventing one would be a
 * guess about notation the user did not ask for.
 */
export function fillTicks(ticks: number): readonly Duration[] | null {
  if (ticks < 0 || !Number.isInteger(ticks)) return null;
  const filled: Duration[] = [];
  let left = ticks;
  while (left > 0) {
    const next = FILLERS.find((duration) => ticksOf(duration) <= left);
    if (next === undefined) return null;
    filled.push(next);
    left -= ticksOf(next);
  }
  return filled;
}
